import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Download, Filter, AlertTriangle, CheckCircle, Clock, XCircle,
  Search, RefreshCw, ChevronRight,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/v1/merch-hub/admin`;

const STATUS_MAP = {
  reserved: { label: 'Reserved', color: '#FBBF24', icon: Clock },
  paid: { label: 'Paid', color: '#10B981', icon: CheckCircle },
  fulfilled: { label: 'Fulfilled', color: '#3B82F6', icon: CheckCircle },
  canceled: { label: 'Canceled', color: '#6B7280', icon: XCircle },
};

const TeamOrdersAdmin = () => {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('academy_token'));

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [filters, setFilters] = useState({ team: 'all', status: 'all', search: '' });
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => {
    if (!token) {
      toast.error('Sign in required.');
      navigate('/academy/login');
      return;
    }
    load();
    loadTeams();
  }, []);  const headers = () => ({ Authorization: `Bearer ${token}` });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.team !== 'all') params.team_slug = filters.team;
      if (filters.status !== 'all') params.status = filters.status;
      const res = await axios.get(`${API}/orders`, { headers: headers(), params });
      setOrders(res.data.orders || []);
      setStats(res.data.stats || null);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Admin only.');
      else if (e.response?.status === 401) navigate('/academy/login');
      else toast.error('Failed to load orders.');
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`, { headers: headers() });
      setTeams(res.data.teams || []);
    } catch (_e) {
      // ignore
    }
  };

  const updateStatus = async (orderId, status) => {
    setBusy((b) => ({ ...b, [orderId]: true }));
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status }, { headers: headers() });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success(`Marked ${status}.`);
    } catch (_e) {
      toast.error('Status update failed.');
    } finally {
      setBusy((b) => ({ ...b, [orderId]: false }));
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (filters.team !== 'all') params.team_slug = filters.team;
      const res = await axios.get(`${API}/orders.csv`, {
        headers: headers(), params, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `team-merch-orders-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported.');
    } catch (_e) {
      toast.error('Export failed.');
    }
  };

  // Client-side search
  const visibleOrders = useMemo(() => {
    if (!filters.search.trim()) return orders;
    const q = filters.search.toLowerCase();
    return orders.filter((o) =>
      [o.player_name, o.parent_name, o.parent_email, o.parent_phone, o.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [orders, filters.search]);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-team-orders">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-bold tracking-[0.4em] text-orange-500 uppercase">Admin Console</div>
            <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mt-2">Team Merch Orders</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="border-2 border-gray-800 hover:border-orange-500 text-gray-300 hover:text-orange-500 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors flex items-center gap-2"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={exportCsv}
              className="bg-orange-500 hover:bg-orange-400 text-black px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors flex items-center gap-2"
              data-testid="admin-export-csv"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stat Strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/5 mb-8" data-testid="admin-stats">
            <Stat label="Orders" value={stats.total_orders} accent="#fff" />
            <Stat label="Units" value={stats.units_total} accent="#fff" />
            <Stat label="Pending Revenue" value={`$${stats.revenue_pending.toFixed(2)}`} accent="#FBBF24" />
            <Stat label="Paid Revenue" value={`$${stats.revenue_paid.toFixed(2)}`} accent="#10B981" />
            <Stat label="Tampered" value={stats.tampered_count} accent={stats.tampered_count > 0 ? '#EF4444' : '#fff'} />
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-6">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input
              type="search"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search player, parent, email, order id..."
              className="w-full bg-[#0a0a0a] border-2 border-gray-800 focus:border-orange-500 outline-none pl-10 pr-3 py-3 font-mono text-sm"
              data-testid="admin-search"
            />
          </div>
          <select
            value={filters.team}
            onChange={(e) => setFilters((f) => ({ ...f, team: e.target.value }))}
            className="md:col-span-3 bg-[#0a0a0a] border-2 border-gray-800 focus:border-orange-500 outline-none px-3 py-3 font-mono text-sm uppercase"
            data-testid="admin-team-filter"
          >
            <option value="all">All Teams</option>
            {teams.map((t) => (
              <option key={t.slug} value={t.slug}>{t.label}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="md:col-span-3 bg-[#0a0a0a] border-2 border-gray-800 focus:border-orange-500 outline-none px-3 py-3 font-mono text-sm uppercase"
            data-testid="admin-status-filter"
          >
            <option value="all">All Statuses</option>
            <option value="reserved">Reserved</option>
            <option value="paid">Paid</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="canceled">Canceled</option>
          </select>
          <button
            onClick={load}
            className="md:col-span-1 border-2 border-gray-800 hover:border-orange-500 text-gray-300 hover:text-orange-500 px-3 py-3 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2"
            data-testid="admin-apply-filters"
          >
            <Filter className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : visibleOrders.length === 0 ? (
          <div className="py-24 text-center text-gray-500 border-2 border-gray-800 border-dashed">No orders match these filters.</div>
        ) : (
          <div className="border-2 border-gray-800">
            {/* Header row */}
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-[#0a0a0a] border-b border-gray-800 text-[9px] font-bold uppercase tracking-[0.25em] text-gray-500">
              <div className="col-span-1">When</div>
              <div className="col-span-2">Team</div>
              <div className="col-span-2">Player</div>
              <div className="col-span-2">Parent</div>
              <div className="col-span-1 text-center">Units</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-1 text-center">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            {visibleOrders.map((o) => {
              const cfg = STATUS_MAP[o.status] || STATUS_MAP.reserved;
              const StatusIcon = cfg.icon;
              const isOpen = expanded[o.id];
              return (
                <div key={o.id} className={`border-b border-gray-800 last:border-b-0 ${o.tampered ? 'bg-red-950/20' : ''}`} data-testid={`admin-order-${o.id}`}>
                  <div className="md:grid md:grid-cols-12 gap-3 px-4 py-4 items-center">
                    <div className="md:col-span-1 text-[11px] font-mono text-gray-400">
                      {new Date(o.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      <div className="text-[9px] text-gray-600">{new Date(o.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-orange-500 font-bold">{o.team_slug}</div>
                      {o.tampered && (
                        <div className="text-[9px] text-red-400 uppercase tracking-widest mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Tampered
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-2 font-bold">{o.player_name}</div>
                    <div className="md:col-span-2 text-sm">
                      <div className="text-gray-200">{o.parent_name}</div>
                      <div className="text-[11px] text-gray-500 font-mono">{o.parent_phone}</div>
                    </div>
                    <div className="md:col-span-1 text-center font-mono font-bold">{o.total_units}</div>
                    <div className="md:col-span-1 text-right font-mono font-bold text-orange-500">${o.verified_total.toFixed(2)}</div>
                    <div className="md:col-span-1 text-center">
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: cfg.color }}>
                        <StatusIcon className="w-3 h-3" /> {cfg.label}
                      </span>
                    </div>
                    <div className="md:col-span-2 flex flex-wrap justify-end gap-2">
                      {o.status === 'reserved' && (
                        <ActionBtn
                          onClick={() => updateStatus(o.id, 'paid')}
                          disabled={busy[o.id]}
                          color="#10B981"
                          testId={`mark-paid-${o.id}`}
                        >Mark Paid</ActionBtn>
                      )}
                      {o.status === 'paid' && (
                        <ActionBtn
                          onClick={() => updateStatus(o.id, 'fulfilled')}
                          disabled={busy[o.id]}
                          color="#3B82F6"
                          testId={`mark-fulfilled-${o.id}`}
                        >Mark Fulfilled</ActionBtn>
                      )}
                      <ActionBtn onClick={() => toggleExpand(o.id)} color="#888">
                        {isOpen ? 'Hide' : 'Detail'} <ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </ActionBtn>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-4 pb-4 pt-2 bg-[#050505] border-t border-gray-800/50" data-testid={`order-detail-${o.id}`}>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.3em] text-gray-500 mb-2">Order Detail</div>
                          {o.products.map((p, i) => (
                            <div key={i} className="border border-gray-800 p-3 mb-2 bg-[#0a0a0a]">
                              <div className="flex justify-between items-baseline">
                                <div className="font-bold text-sm">{p.name}</div>
                                <div className="font-mono text-sm">${p.line_total.toFixed(2)}</div>
                              </div>
                              <div className="text-[11px] text-gray-500 uppercase tracking-widest mt-1">{p.subtitle}</div>
                              <div className="text-xs text-gray-300 mt-2">
                                {p.selections.map((s) => `(${s.quantity}) ${s.size_label}`).join(', ')}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="text-[11px] space-y-1 font-mono text-gray-400">
                          <Row k="Order ID" v={o.id} />
                          <Row k="Parent Email" v={o.parent_email || '—'} />
                          <Row k="Client Total" v={`$${o.client_total.toFixed(2)}`} flag={o.tampered} />
                          <Row k="Verified Total" v={`$${o.verified_total.toFixed(2)}`} accent />
                          <Row k="Submitted" v={new Date(o.created_at).toLocaleString()} />
                          {o.updated_at && <Row k="Updated" v={new Date(o.updated_at).toLocaleString()} />}
                          {o.updated_by && <Row k="Updated By" v={o.updated_by} />}
                          <div className="pt-3 mt-3 border-t border-gray-800 flex gap-2">
                            {['reserved', 'paid', 'fulfilled', 'canceled'].filter((s) => s !== o.status).map((s) => (
                              <button
                                key={s}
                                onClick={() => updateStatus(o.id, s)}
                                disabled={busy[o.id]}
                                className="border border-gray-800 hover:border-orange-500 text-gray-400 hover:text-orange-500 px-2 py-1 text-[9px] uppercase tracking-widest"
                              >
                                Mark {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="bg-black p-5 lg:p-6 text-center">
    <div className="text-3xl md:text-4xl font-black font-mono mb-1" style={{ color: accent }}>{value}</div>
    <div className="text-[9px] text-gray-500 uppercase tracking-[0.25em]">{label}</div>
  </div>
);

const ActionBtn = ({ children, color, onClick, disabled, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors disabled:opacity-40 flex items-center gap-1"
    style={{ borderColor: `${color}66`, color }}
    data-testid={testId}
  >
    {children}
  </button>
);

const Row = ({ k, v, accent, flag }) => (
  <div className="flex justify-between gap-3">
    <span className="text-gray-600 uppercase tracking-widest text-[9px]">{k}</span>
    <span className={flag ? 'text-red-400 line-through' : accent ? 'text-orange-500 font-bold' : 'text-gray-300'}>{v}</span>
  </div>
);

export default TeamOrdersAdmin;
